import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AddUserForm, useResendInvitation } from '@/features/auth';
import {
  can,
  useIdentity,
  DELETE_USER_PERMISSION,
  DISABLE_USER_PERMISSION,
  ADMIN_PERMISSION,
} from '@/entities/session';

import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import {
  useDeleteUser,
  useRoles,
  useSetRoles,
  useSetStatus,
  useTenantUsers,
} from '../api/access';
import {
  canDisable,
  lastSeen,
  orderRoles,
  mayResendInvitation,
  orderUsers,
  statusLabelKey,
  statusTone,
} from '../model/access';
import { UserDrawer } from './UserDrawer';

import type { TenantUser } from '@/shared/api/generated/tenancy';

/* Who can sign in to this tenant, and what their role lets them do — as the design draws it: a
 * header with Add user, one row of filters, the people table, and a side panel per person.
 *
 * **Everything on this screen is backed by a real route.** The user listing, role replacement,
 * enable/disable, deletion, invitation resend and the role list under `/v1/auth` were built for
 * exactly this surface.
 *
 * **What is deliberately not here**, because the design has none of it: the row of links to other
 * configuration screens (the navigation already reaches them), the standing note about how quickly
 * a role change takes effect, and the roles table with its permission matrix. The panel names each
 * role with what it is meant for, which is the part of that table a person deciding a grant reads.
 *
 * **Filtering is client-side**, which is right here where it is wrong on the catalogue: a tenant's
 * user list is bounded by how many people work at the customer, `GET /v1/auth/users` returns all of
 * them, and filtering in the browser is instant. Matched against the email *and* the display name,
 * because an administrator looking for somebody types whichever they remember.
 */

interface Flash {
  readonly tone: 'ok' | 'warn' | 'error';
  readonly title: string;
  readonly body?: string;
}

export function AdminScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const users = useTenantUsers();
  const roles = useRoles();
  const setRoles = useSetRoles();
  const setStatus = useSetStatus();
  const deleteUser = useDeleteUser();
  const resend = useResendInvitation();

  const [addOpen, setAddOpen] = useState(false);
  const [managing, setManaging] = useState<string | null>(null);
  /* Off by default, as the design has it: a disabled account cannot sign in, and the question this
     screen is usually opened for is who can. The checkbox brings them back. */
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [flash, setFlash] = useState<Flash | null>(null);
  const [now] = useState(() => new Date());

  const callerSubject = identity.data?.subject ?? '';
  const mayDisable = can(identity.data, DISABLE_USER_PERMISSION);
  const mayGrant = can(identity.data, ADMIN_PERMISSION);
  const mayDelete = can(identity.data, DELETE_USER_PERMISSION);

  const allUsers = users.isSuccess ? orderUsers(users.data.items) : [];
  const term = search.trim().toLowerCase();
  const visible = allUsers.filter(
    (user) =>
      (includeDisabled || !user.disabled) &&
      (roleFilter === '' || user.roles.includes(roleFilter)) &&
      (term === '' ||
        user.subject.toLowerCase().includes(term) ||
        user.display_name.toLowerCase().includes(term)),
  );
  const roleList = roles.isSuccess ? orderRoles(roles.data.items) : [];
  /* Looked up from the listing rather than held, so the panel shows the server's answer after a save
     rather than the row as it was when Manage was pressed. */
  const managed: TenantUser | undefined = allUsers.find(
    (user) => user.subject === managing,
  );

  const sendInvitation = (user: TenantUser): void => {
    setFlash(null);
    resend.mutate(user.subject, {
      onSuccess: (result) => {
        setFlash(
          result.delivered
            ? {
                tone: 'ok',
                title: t('admin.resend.sentTitle'),
                body: t('admin.resend.sentBody', {
                  hours: Math.max(1, Math.round(result.expires_in_minutes / 60)),
                  email: user.subject,
                }),
              }
            : { tone: 'warn', title: t('admin.resendUndeliverable') },
        );
      },
      onError: () => {
        setFlash({ tone: 'error', title: t('admin.resendFailed') });
      },
    });
  };

  return (
    <div className="lq-ua">
      <PageHeader
        title={t('admin.title')}
        lede={t('admin.lede')}
        actions={
          mayGrant ? (
            <Button
              tone="primary"
              onClick={() => {
                setAddOpen(true);
              }}
            >
              {t('admin.addUser')}
            </Button>
          ) : undefined
        }
      />

      {flash !== null && (
        <div className="lq-ua__flash" data-tone={flash.tone} role="status">
          <p className="lq-ua__flashText">
            <strong>{flash.title}</strong>
            {flash.body === undefined ? null : ` ${flash.body}`}
          </p>
          <button
            type="button"
            className="lq-ua__flashClose"
            aria-label={t('common.dismiss')}
            onClick={() => {
              setFlash(null);
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="lq-ua__filters">
        <input
          className="lq-input lq-ua__search"
          type="search"
          value={search}
          aria-label={t('admin.filter.search')}
          placeholder={t('admin.filter.searchPlaceholder')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <select
          className="lq-input lq-ua__role"
          value={roleFilter}
          aria-label={t('admin.filter.role')}
          onChange={(event) => {
            setRoleFilter(event.target.value);
          }}
        >
          <option value="">{t('admin.filter.allRoles')}</option>
          {roleList.map((role) => (
            <option key={role.name} value={role.name}>
              {role.name}
            </option>
          ))}
        </select>
        <label className="lq-ua__check">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(event) => {
              setIncludeDisabled(event.target.checked);
            }}
          />
          {t('admin.filter.includeDisabled')}
        </label>
        <span className="lq-ua__spacer" />
        <span className="lq-ua__count">
          {t('admin.users.count', { shown: visible.length, total: allUsers.length })}
        </span>
      </div>

      {users.isPending ? (
        <SkeletonRows rows={5} />
      ) : users.isError ? (
        /* A read that returns nothing is a failure, and saying "no users" would be a claim about
           the tenant rather than a report about the request. */
        <UnavailableState
          title={t('admin.users.unreadable')}
          guidance={users.error.message}
        />
      ) : (
        <div className="lq-card lq-ua__card">
          <table className="lq-ua__table" aria-label={t('admin.users.caption')}>
            <thead>
              <tr>
                <th scope="col">{t('admin.column.user')}</th>
                <th scope="col">{t('admin.column.roles')}</th>
                <th scope="col">{t('admin.column.lastSeen')}</th>
                <th scope="col">{t('admin.column.status')}</th>
                <th scope="col">
                  <span className="lq-visually-hidden">{t('admin.column.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((user) => (
                <tr key={user.subject}>
                  <td data-label={t('admin.column.user')}>
                    <div className="lq-ua__name">{user.display_name}</div>
                    <div className="lq-ua__email">{user.subject}</div>
                  </td>
                  <td data-label={t('admin.column.roles')}>
                    {user.roles.length === 0 ? (
                      /* Words rather than an empty cell: a person with no roles is something an
                         administrator has to act on, and the normal state of somebody invited. */
                      <span className="lq-ua__noRoles">{t('admin.noRoles')}</span>
                    ) : (
                      <span className="lq-ua__roles">
                        {user.roles.map((role) => (
                          <span key={role} className="lq-ua__roleChip">
                            {role}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td data-label={t('admin.column.lastSeen')} className="lq-ua__seen">
                    <LastSeenCell lastLoginAt={user.last_login_at} now={now} />
                  </td>
                  <td data-label={t('admin.column.status')}>
                    <span
                      className="lq-ua__status"
                      data-tone={statusTone(user)}
                      title={
                        user.disabled
                          ? t('admin.status.disabledMeaning')
                          : mayResendInvitation(user)
                            ? t('admin.status.pendingMeaning')
                            : undefined
                      }
                    >
                      {t(statusLabelKey(user))}
                    </span>
                  </td>
                  <td className="lq-ua__actions">
                    {/* Offered only for an invited account. The server answers 409 for one that
                        already has a password, because re-inviting it could only mean clearing it. */}
                    {mayGrant && mayResendInvitation(user) && (
                      <Button
                        size="compact"
                        busy={resend.isPending && resend.variables === user.subject}
                        onClick={() => {
                          sendInvitation(user);
                        }}
                      >
                        {t('admin.resendInvitation')}
                      </Button>
                    )}
                    {mayGrant && (
                      <Button
                        size="compact"
                        onClick={() => {
                          setFlash(null);
                          setRoles.reset();
                          setStatus.reset();
                          setManaging(user.subject);
                        }}
                      >
                        {t('admin.manage')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visible.length === 0 ? (
            <p className="lq-ua__none">{t('admin.users.noMatch')}</p>
          ) : null}
        </div>
      )}

      <Modal
        open={addOpen}
        title={t('auth.team.title')}
        hint={t('auth.team.hint')}
        className="lq-ua__addm"
        onClose={() => {
          setAddOpen(false);
        }}
      >
        <AddUserForm
          /* The dialog stays open on success: `welcome_email` says whether the invitation was
             delivered, and with no mail transport only the administrator can tell the new person —
             the form renders that notice, so closing on success would unmount it unread. */
          onAdded={() => {
            void users.refetch();
          }}
          onCancel={() => {
            setAddOpen(false);
          }}
        />
      </Modal>

      {managed !== undefined && (
        <UserDrawer
          key={managed.subject}
          user={managed}
          roles={roleList}
          callerSubject={callerSubject}
          mayDisable={mayDisable && canDisable(managed, callerSubject)}
          mayDelete={mayDelete}
          onClose={() => {
            setManaging(null);
            /* Reset on close, or reopening the panel for a different person would show the previous
               delete's reason. */
            deleteUser.reset();
          }}
          onSaveRoles={(roleNames) => {
            setRoles.mutate({ subject: managed.subject, roleNames });
          }}
          onSetDisabled={(disabled) => {
            setStatus.mutate({ subject: managed.subject, disabled });
          }}
          onDelete={() => {
            deleteUser.mutate(managed.subject, {
              onSuccess: () => {
                setManaging(null);
                setFlash({
                  tone: 'ok',
                  title: t('admin.deleted.title', { name: managed.display_name }),
                  body: t('admin.deleted.body'),
                });
              },
            });
          }}
          saved={setRoles.isSuccess}
          saving={setRoles.isPending || setStatus.isPending}
          error={setRoles.error ?? setStatus.error}
          deleting={deleteUser.isPending}
          deleteError={deleteUser.error}
        />
      )}
    </div>
  );
}

function LastSeenCell({
  lastLoginAt,
  now,
}: {
  readonly lastLoginAt: string | null;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const seen = lastSeen(lastLoginAt, now);

  if (seen.kind === 'never') {
    /* Words, never a dash and never an epoch: "never signed in" is what somebody chasing an
       onboarding acts on. */
    return <span>{t('admin.neverSignedIn')}</span>;
  }
  if (seen.kind === 'today') {
    return (
      <span title={seen.at.toLocaleString()}>
        {t('admin.lastSeen.today', { clock: clockTime(seen.at) })}
      </span>
    );
  }
  if (seen.kind === 'yesterday') {
    return (
      <span title={seen.at.toLocaleString()}>
        {t('admin.lastSeen.yesterday', { clock: clockTime(seen.at) })}
      </span>
    );
  }
  return (
    <span title={seen.at.toLocaleString()}>
      {t('admin.lastSeen.earlier', { at: seen.at })}
    </span>
  );
}

function clockTime(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
